import jsPDF from 'jspdf';
import { applyPlugin } from 'jspdf-autotable';
import * as XLSX from 'xlsx';

// Restore doc.autoTable() and doc.lastAutoTable API (v3 compat)
applyPlugin(jsPDF);

const BRAND_COLOR = [0, 53, 148]; // #003594

export function generateCredentialsPDF(data) {
  const doc = new jsPDF();
  const { merchant, project, api_keys, environments } = data;
  
  // Header
  doc.setFillColor(...BRAND_COLOR);
  doc.rect(0, 0, 210, 40, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(22);
  doc.setFont('helvetica', 'bold');
  doc.text('Caura Pay', 15, 18);
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.text('Credenciales de Integracion', 15, 26);
  doc.setFontSize(8);
  doc.text(`Generado: ${new Date().toLocaleDateString('es-VE')}`, 15, 33);
  doc.text('CONFIDENCIAL', 170, 33);

  let y = 50;

  // Merchant Info
  doc.setTextColor(0, 53, 148);
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.text('Datos del Comercio', 15, y);
  y += 8;

  doc.setTextColor(60, 60, 60);
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');

  const merchantRows = [
    ['Empresa', merchant.company_name || ''],
    ['Email', merchant.email || ''],
    ['RIF', merchant.rif || 'No asignado'],
    ['Telefono', merchant.phone || 'No asignado'],
  ];
  if (merchant.password) {
    merchantRows.push(['Contrasena (temporal)', merchant.password]);
  }

  doc.autoTable({
    startY: y,
    body: merchantRows,
    theme: 'plain',
    styles: { fontSize: 10, cellPadding: 3 },
    columnStyles: { 0: { fontStyle: 'bold', cellWidth: 50, textColor: [80, 80, 80] } },
    margin: { left: 15 },
  });

  y = doc.lastAutoTable.finalY + 12;

  // Project Info
  if (project) {
    doc.setTextColor(0, 53, 148);
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('Proyecto', 15, y);
    y += 8;

    doc.autoTable({
      startY: y,
      body: [
        ['Nombre', project.project_name || ''],
        ['ID', project.project_id || ''],
      ],
      theme: 'plain',
      styles: { fontSize: 10, cellPadding: 3 },
      columnStyles: { 0: { fontStyle: 'bold', cellWidth: 50, textColor: [80, 80, 80] } },
      margin: { left: 15 },
    });

    y = doc.lastAutoTable.finalY + 12;
  }

  // API Keys
  doc.setTextColor(0, 53, 148);
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.text('API Keys', 15, y);
  y += 4;

  const keyRows = [];
  if (api_keys?.test) {
    keyRows.push(['QA / Test', api_keys.test.full_key || api_keys.test.prefix || '', 'cpt_test_...']);
  }
  if (api_keys?.live) {
    keyRows.push(['Produccion', api_keys.live.full_key || api_keys.live.prefix || '', 'cpt_live_...']);
  }
  // If api_keys is an array (from credentials endpoint)
  if (Array.isArray(api_keys)) {
    api_keys.forEach(k => {
      keyRows.push([
        k.environment === 'test' ? 'QA / Test' : 'Produccion',
        k.full_key || k.prefix || '',
        k.name || '',
      ]);
    });
  }

  if (keyRows.length > 0) {
    doc.autoTable({
      startY: y + 4,
      head: [['Entorno', 'API Key', 'Tipo']],
      body: keyRows,
      theme: 'striped',
      styles: { fontSize: 9, cellPadding: 4 },
      headStyles: { fillColor: BRAND_COLOR, textColor: [255, 255, 255] },
      margin: { left: 15 },
    });
    y = doc.lastAutoTable.finalY + 12;
  }

  // Environments
  doc.setTextColor(0, 53, 148);
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.text('Entornos de Integracion', 15, y);
  y += 4;

  doc.autoTable({
    startY: y + 4,
    head: [['', 'QA / Pruebas', 'Produccion']],
    body: [
      ['URL API', environments?.qa?.api_url || 'caurapay.com/api', environments?.production?.api_url || 'caurapay.com/api'],
      ['API Key', 'cpt_test_...', 'cpt_live_...'],
      ['Transacciones', 'Simuladas', 'Reales'],
    ],
    theme: 'grid',
    styles: { fontSize: 9, cellPadding: 4 },
    headStyles: { fillColor: BRAND_COLOR, textColor: [255, 255, 255] },
    columnStyles: { 0: { fontStyle: 'bold', cellWidth: 40 } },
    margin: { left: 15 },
  });

  y = doc.lastAutoTable.finalY + 12;

  // Footer
  doc.setFillColor(240, 240, 240);
  doc.rect(15, y, 180, 25, 'F');
  doc.setTextColor(100, 100, 100);
  doc.setFontSize(8);
  doc.text('IMPORTANTE: Este documento contiene credenciales sensibles.', 20, y + 7);
  doc.text('No compartir publicamente. Las API Keys de produccion (cpt_live_) generan transacciones reales.', 20, y + 13);
  doc.text('Documentacion completa: caurapay.com/documentation', 20, y + 19);

  const filename = `CauraPay_Credenciales_${(merchant.company_name || 'comercio').replace(/\s+/g, '_')}.pdf`;
  doc.save(filename);
}

export function generateCredentialsExcel(data) {
  const { merchant, project, api_keys, environments } = data;

  // Sheet 1: Merchant Info
  const merchantSheet = [
    ['CAURA PAY - CREDENCIALES DE INTEGRACION'],
    [`Generado: ${new Date().toLocaleDateString('es-VE')}`],
    [],
    ['DATOS DEL COMERCIO'],
    ['Empresa', merchant.company_name || ''],
    ['Email', merchant.email || ''],
    ['RIF', merchant.rif || 'No asignado'],
    ['Telefono', merchant.phone || 'No asignado'],
  ];
  if (merchant.password) {
    merchantSheet.push(['Contrasena (temporal)', merchant.password]);
  }

  // Sheet 2: API Keys & Project
  const keysSheet = [
    ['PROYECTO'],
    ['Nombre', project?.project_name || ''],
    ['ID', project?.project_id || ''],
    [],
    ['API KEYS'],
    ['Entorno', 'API Key', 'Prefijo', 'Nombre'],
  ];

  if (api_keys?.test) {
    keysSheet.push(['QA / Test', api_keys.test.full_key || '', api_keys.test.prefix || 'cpt_test_...', 'Key de pruebas']);
  }
  if (api_keys?.live) {
    keysSheet.push(['Produccion', api_keys.live.full_key || '', api_keys.live.prefix || 'cpt_live_...', 'Key de produccion']);
  }
  if (Array.isArray(api_keys)) {
    api_keys.forEach(k => {
      keysSheet.push([
        k.environment === 'test' ? 'QA / Test' : 'Produccion',
        k.full_key || k.prefix || '',
        k.prefix || '',
        k.name || '',
      ]);
    });
  }

  // Sheet 3: Environments
  const envSheet = [
    ['ENTORNOS DE INTEGRACION'],
    [],
    ['Parametro', 'QA / Pruebas', 'Produccion'],
    ['URL API', environments?.qa?.api_url || 'caurapay.com/api', environments?.production?.api_url || 'caurapay.com/api'],
    ['Prefijo API Key', 'cpt_test_...', 'cpt_live_...'],
    ['Transacciones', 'Simuladas / Sin costo', 'Reales / Con costo'],
    [],
    ['IMPORTANTE'],
    ['Este documento contiene credenciales sensibles. No compartir publicamente.'],
    ['Las API Keys de produccion (cpt_live_) generan transacciones reales.'],
    ['Documentacion: caurapay.com/documentation'],
  ];

  const wb = XLSX.utils.book_new();

  const ws1 = XLSX.utils.aoa_to_sheet(merchantSheet);
  ws1['!cols'] = [{ wch: 25 }, { wch: 50 }];
  XLSX.utils.book_append_sheet(wb, ws1, 'Comercio');

  const ws2 = XLSX.utils.aoa_to_sheet(keysSheet);
  ws2['!cols'] = [{ wch: 15 }, { wch: 45 }, { wch: 20 }, { wch: 25 }];
  XLSX.utils.book_append_sheet(wb, ws2, 'API Keys');

  const ws3 = XLSX.utils.aoa_to_sheet(envSheet);
  ws3['!cols'] = [{ wch: 20 }, { wch: 35 }, { wch: 35 }];
  XLSX.utils.book_append_sheet(wb, ws3, 'Entornos');

  const filename = `CauraPay_Credenciales_${(merchant.company_name || 'comercio').replace(/\s+/g, '_')}.xlsx`;
  XLSX.writeFile(wb, filename);
}
